/* global BigInt */
// 與 POS／後端 Phase 1 規則對齊；所有金額以整數計算，百分比使用十進位比例。
export function compareCouponIds(a, b) {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function createCouponContractError() {
  const error = new Error("優惠券功能暫時無法使用，請稍後再試");
  error.code = "COUPON_INVALID_CONTRACT";
  return error;
}

const isId = (value) => typeof value === "string" && value.trim().length > 0;
const isInteger = (value, min = 0) => Number.isSafeInteger(value) && value >= min;
const fail = () => { throw createCouponContractError(); };
const sum = (values) => values.reduce((total, value) => total + value, 0);
const samePath = (a, b) => JSON.stringify(a || []) === JSON.stringify(b || []);

export function checkCouponNormalizedOrder(order) {
  if (!Array.isArray(order?.items) || !order.items.length) fail();
  const ids = new Set();
  for (const line of order.items) {
    if (!isId(line.order_line_id) || ids.has(line.order_line_id) ||
        !["product", "combo"].includes(line.root_type) || !isId(line.root_id) ||
        !isInteger(line.quantity, 1) || !Number.isSafeInteger(line.unit_merchandise_amount)) fail();
    ids.add(line.order_line_id);
    const paths = new Set();
    for (const child of line.components || []) {
      if (line.root_type !== "combo" || !Array.isArray(child.component_path) ||
          !child.component_path.length || !child.component_path.every(isId) || !isId(child.product_id)) fail();
      const key = JSON.stringify(child.component_path);
      if (paths.has(key)) fail();
      paths.add(key);
    }
    const options = new Set();
    for (const option of line.spec_options || []) {
      if (!isId(option.spec_id) || !isId(option.option_id) ||
          !Number.isSafeInteger(option.unit_surcharge_amount ?? 0)) fail();
      if (option.component_path && (!Array.isArray(option.component_path) ||
          !option.component_path.length || !option.component_path.every(isId) ||
          !paths.has(JSON.stringify(option.component_path)))) fail();
      const key = JSON.stringify([option.spec_id, option.option_id, option.component_path || []]);
      if (options.has(key)) fail();
      options.add(key);
    }
  }
  for (const [key, value] of Object.entries(order.fees || {})) {
    if (!["service_fee", "delivery_fee", "card_fee", "other_fee"].includes(key) || !isInteger(value)) fail();
  }
  return order;
}

function checkTarget(target, isRequired) {
  if (!target || !["product", "combo", "spec_option"].includes(target.target_type)) fail();
  if (target.target_type === "spec_option") {
    if (!isRequired || !["product", "combo"].includes(target.root_type) || !isId(target.root_id) ||
        !isId(target.spec_id) || !isId(target.option_id) ||
        (target.component_path != null && (!Array.isArray(target.component_path) || !target.component_path.every(isId)))) fail();
  } else if (!isId(target.target_id)) fail();
  if (isRequired && !isInteger(target.required_quantity, 1)) fail();
}

// 付原價比例最多四位小數。BigInt 避免 binary float 在 .5 邊界改變捨入。
function getDiscountRate(payRate) {
  const text = String(payRate);
  if (!/^\d+(\.\d{1,4})?$/.test(text)) fail();
  const [whole, decimal = ""] = text.split(".");
  const scale = BigInt(10000);
  const paid = BigInt(whole) * scale + BigInt(decimal.padEnd(4, "0"));
  const denominator = BigInt(100) * scale;
  if (paid <= BigInt(0) || paid >= denominator) fail();
  return { numerator: denominator - paid, denominator };
}

function matches(line, target) {
  if (target.target_type !== "spec_option") {
    return line.root_type === target.target_type && line.root_id === target.target_id;
  }
  return line.root_type === target.root_type && line.root_id === target.root_id &&
    (line.spec_options || []).some((option) => option.spec_id === target.spec_id &&
      option.option_id === target.option_id && samePath(option.component_path, target.component_path));
}

export function getCouponCalculation({ currentPublicRules: rules, normalizedOrder }) {
  checkCouponNormalizedOrder(normalizedOrder);
  if (!rules || typeof rules !== "object") fail();
  // 清單 public_summary 可能只有部分欄位：缺值給後端預設值，有值才嚴格檢查。
  const requiredTargets = rules.required_targets ?? [];
  const benefitTargets = rules.benefit_targets ?? [];
  // schema_version 不決定可用性；只檢查實際優惠規則。
  if (!["fixed_amount", "percentage", "target_exchange"].includes(rules.benefit_type) ||
      !["order", "target"].includes(rules.benefit_scope) || rules.spec_benefit_mode != null ||
      (rules.required_target_match_mode ?? "all") !== "all" ||
      (rules.benefit_quantity_mode != null && rules.benefit_quantity_mode !== "fixed") ||
      !Array.isArray(requiredTargets) || requiredTargets.length > 1 || !Array.isArray(benefitTargets) ||
      !isInteger(rules.min_order_amount ?? 0)) fail();
  const isTarget = rules.benefit_scope === "target";
  const isExchange = rules.benefit_type === "target_exchange";
  if ((isExchange && !isTarget) || (isTarget && (benefitTargets.length !== 1 || !isInteger(rules.benefit_quantity, 1))) ||
      (!isTarget && benefitTargets.length)) fail();
  requiredTargets.forEach((target) => checkTarget(target, true));
  benefitTargets.forEach((target) => checkTarget(target, false));
  const fixedAmount = isTarget ? rules.target_unit_discount_amount : rules.order_discount_amount;
  if (rules.benefit_type === "fixed_amount" && !isInteger(fixedAmount, 1)) fail();
  const rate = rules.benefit_type === "percentage" ? getDiscountRate(rules.pay_rate_percent) : null;

  const lines = [...normalizedOrder.items].sort((a, b) => compareCouponIds(a.order_line_id, b.order_line_id));
  const merchandiseBase = (line) => line.unit_merchandise_amount + sum((line.spec_options || []).map((o) => o.unit_surcharge_amount ?? 0));
  const base = (line) => isTarget ? line.unit_merchandise_amount : merchandiseBase(line);
  const target = benefitTargets[0];
  let eligible = lines.filter((line) => base(line) > 0 && (!isTarget || matches(line, target)));
  if (isTarget) eligible.sort((a, b) => Number(b.coupon_benefit === true) - Number(a.coupon_benefit === true) ||
    base(a) - base(b) || compareCouponIds(a.order_line_id, b.order_line_id));
  let remainingQuantity = isTarget ? rules.benefit_quantity : Infinity;
  const selected = eligible.map((line) => {
    const quantity = Math.min(line.quantity, remainingQuantity);
    remainingQuantity -= quantity;
    return { line, quantity, unit: base(line) };
  }).filter((item) => item.quantity > 0);
  const exchanged = new Map(isExchange ? selected.map((s) => [s.line.order_line_id, s.quantity]) : []);
  // 兌換僅排除本體；規格仍在門檻。
  const threshold = sum(lines.map((line) => line.quantity * merchandiseBase(line) -
    (exchanged.get(line.order_line_id) || 0) * line.unit_merchandise_amount));
  const unmetConditions = [];
  if (threshold < (rules.min_order_amount ?? 0)) unmetConditions.push("MIN_ORDER_AMOUNT_NOT_MET");
  for (const requirement of requiredTargets) {
    const count = sum(lines.filter((line) => matches(line, requirement)).map((line) =>
      line.quantity - (exchanged.get(line.order_line_id) || 0)));
    if (count < requirement.required_quantity) unmetConditions.push("REQUIRED_TARGET_QUANTITY_NOT_MET");
  }
  if (!selected.length) unmetConditions.push(isTarget ? "BENEFIT_TARGET_NOT_IN_ORDER" : "ORDER_ITEMS_EMPTY");
  const empty = { isApplicable: false, couponDiscountAmount: 0, actualBenefitItems: [], unmetConditions };
  if (unmetConditions.length) return empty;

  let discounts;
  if (isExchange) discounts = selected.map((s) => s.unit * s.quantity);
  else if (rate) {
    const amounts = selected.map((s) => BigInt(s.unit) * BigInt(s.quantity));
    const total = amounts.reduce((a, b) => a + b, BigInt(0));
    const discount = (total * rate.numerator * BigInt(2) + rate.denominator) / (rate.denominator * BigInt(2));
    const shares = amounts.map((amount, index) => ({ index, value: amount * discount / total, remainder: amount * discount % total }));
    let residue = discount - shares.reduce((a, b) => a + b.value, BigInt(0));
    const ranked = [...shares].sort((a, b) => a.remainder > b.remainder ? -1 : a.remainder < b.remainder ? 1 :
      compareCouponIds(selected[a.index].line.order_line_id, selected[b.index].line.order_line_id));
    for (const share of ranked) {
      if (residue <= BigInt(0)) break;
      share.value += BigInt(1);
      residue -= BigInt(1);
    }
    discounts = shares.map((share) => Number(share.value));
  } else if (isTarget) discounts = selected.map((s) => Math.min(fixedAmount, s.unit) * s.quantity);
  else {
    let remaining = fixedAmount;
    discounts = selected.map((s) => {
      const value = Math.min(remaining, s.unit * s.quantity);
      remaining -= value;
      return value;
    });
  }
  const actualBenefitItems = selected.map((s, index) => ({
    order_line_id: s.line.order_line_id,
    target_type: s.line.root_type,
    target_id: s.line.root_id,
    quantity: s.quantity,
    unit_merchandise_amount: s.unit,
    discount_amount: discounts[index],
  })).filter((item) => item.discount_amount > 0);
  const couponDiscountAmount = sum(discounts);
  if (!Number.isSafeInteger(couponDiscountAmount)) fail();
  return { isApplicable: couponDiscountAmount > 0, couponDiscountAmount, actualBenefitItems, unmetConditions };
}
